/**
 * Merge dialog — Freshdesk's "Merging two or more conversations together" flow.
 *
 * The agent picks the conversations to merge (via ConversationSearchDrawer), then
 * this step decides how:
 *   • which conversation survives as the PRIMARY — a radio over the whole set, not
 *     just "whichever one you happened to open";
 *   • remove any conversation from the set with the minus button;
 *   • optionally carry the secondaries' recipients over as CC;
 *   • an optional note, public (requester sees it) or private (agents only).
 *
 * The consequences are stated plainly up front, because merging moves data and
 * closes conversations and there is no undo.
 */
import { useMemo, useState, type JSX } from 'react'
import { GitMerge, Minus, AlertTriangle, Loader2, Check } from 'lucide-react'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, R, textareaClass } from './primitives.js'

export interface MergeCandidate {
  id: string
  referenceId: string
  subject: string | null
  status: string | null
  /** Requester/client label — used to warn when the set spans several people. */
  requester: string | null
  /* ── Merge eligibility, mirroring the server's assertMergeable(). Every
     field is optional: a caller that cannot supply them simply gets no
     pre-flight check, and the server's 409 still explains the block. ── */
  parentReferenceId?: string | null | undefined
  childCount?: number | undefined
  /** Fallbacks for callers that know a conversation is in a hierarchy but not
   *  the reference/count — the conversation detail response, for instance,
   *  carries `parentId`/`isParent` but no parent reference. */
  hasParent?: boolean | undefined
  hasChildren?: boolean | undefined
  mergedIntoReferenceId?: string | null | undefined
  secondaryCount?: number | undefined
  linkedRelationCount?: number | undefined
  approvalStatus?: string | null | undefined
  approvalStateId?: string | null | undefined
}

/** A rule that stops this conversation being merged, in the agent's terms. */
export interface MergeBlocker {
  /** The server code this mirrors, so the pre-flight and 409 paths agree. */
  code: string
  /** What is wrong with this specific conversation. */
  reason: string
  /** What the agent can do about it. */
  remedy: string
}

/**
 * Why `c` cannot take part in this merge, or null if it can.
 *
 * Mirrors assertMergeable() in packages/conversations — the server is still the
 * authority, but repeating the rules here means the agent is told *which*
 * conversation is blocked and what to do about it while the dialog is open,
 * rather than after a failed submit. `role` matters: a conversation that is
 * already a primary can absorb more secondaries, but cannot itself become one.
 */
export function mergeBlocker(c: MergeCandidate, role: 'primary' | 'secondary'): MergeBlocker | null {
  const ref = `#${c.referenceId}`
  if (c.mergedIntoReferenceId) {
    return role === 'primary'
      ? {
          code: 'PRIMARY_IS_SECONDARY',
          reason: `${ref} has already been merged into #${c.mergedIntoReferenceId}.`,
          remedy: `Keep #${c.mergedIntoReferenceId} instead, or pick a different conversation as the primary.`,
        }
      : {
          code: 'ALREADY_SECONDARY',
          reason: `${ref} has already been merged into #${c.mergedIntoReferenceId}.`,
          remedy: 'Remove it from this merge — its content already lives on that conversation.',
        }
  }
  if (role === 'secondary' && (c.secondaryCount ?? 0) > 0) {
    return {
      code: 'ALREADY_PRIMARY',
      reason: `${ref} is already the primary of ${c.secondaryCount} merged conversation${c.secondaryCount === 1 ? '' : 's'}.`,
      remedy: 'Make it the primary of this merge, or remove it — a primary cannot be folded into another conversation.',
    }
  }
  if (c.parentReferenceId || c.hasParent) {
    return {
      code: 'IN_PARENT_CHILD_RELATION',
      reason: c.parentReferenceId
        ? `${ref} is a child of #${c.parentReferenceId}.`
        : `${ref} is a child of another conversation.`,
      remedy: 'Detach it from its parent on the Parent/Child tab first — merge does not carry hierarchies.',
    }
  }
  if ((c.childCount ?? 0) > 0 || c.hasChildren) {
    return {
      code: 'IN_PARENT_CHILD_RELATION',
      reason: c.childCount
        ? `${ref} is the parent of ${c.childCount} conversation${c.childCount === 1 ? '' : 's'}.`
        : `${ref} is a parent conversation with children of its own.`,
      remedy: 'Detach its children on the Parent/Child tab first — merge does not carry hierarchies.',
    }
  }
  if ((c.linkedRelationCount ?? 0) > 0) {
    return {
      code: 'IN_LINK_RELATION',
      reason: `${ref} is linked to ${c.linkedRelationCount} other conversation${c.linkedRelationCount === 1 ? '' : 's'}.`,
      remedy: 'Remove those links on the Linked tab, then merge.',
    }
  }
  if (c.approvalStatus === 'pending' || c.approvalStateId) {
    return {
      code: 'APPROVAL_PENDING',
      reason: `${ref} is waiting on an approval decision.`,
      remedy: 'Approve or reject the open request first — merging would move the message it is waiting on.',
    }
  }
  return null
}

/** Outcome of the merge request, so a server-side rejection can be shown in
 *  the dialog rather than only as a toast that scrolls away. */
export type MergeResult =
  | { ok: true }
  | { ok: false; failure?: { code?: string | undefined; conversationId?: string | undefined } | undefined }

/** Server codes that name no specific conversation — rendered as one message. */
const SERVER_ONLY_REASONS: Record<string, string> = {
  SELF_MERGE_NOT_ALLOWED: 'A conversation cannot be merged into itself.',
  NO_SECONDARY_CONVERSATIONS: 'Select at least one conversation to merge in.',
  PRIMARY_CONVERSATION_NOT_FOUND: 'The conversation you chose to keep no longer exists — reload and try again.',
  SECONDARY_CONVERSATION_NOT_FOUND: 'One of the selected conversations no longer exists — reload and try again.',
  TOO_MANY_SECONDARIES: 'At most 15 conversations can be merged at once.',
  DIFFERENT_CLIENT: 'These conversations belong to different requesters. Tick “Merge anyway” to continue.',
}

export interface MergeSubmit {
  primaryId: string
  secondaryIds: string[]
  note: string
  noteIsPublic: boolean
  addSecondaryRecipientsToCc: boolean
  allowDifferentRequesters: boolean
}

export interface MergeDrawerProps {
  open: boolean
  onClose: () => void
  /** Every conversation in the merge, including the one the agent started from. */
  candidates: MergeCandidate[]
  /** Pre-selected primary (usually the open conversation). */
  defaultPrimaryId: string
  /** Resolve ok:true to close the drawer; ok:false keeps it open and its
   *  `failure` is explained in place. */
  onConfirm: (input: MergeSubmit) => Promise<MergeResult>
}

const STATUS_PILL: Record<string, string> = {
  open: 'bg-emerald-50 text-emerald-700',
  reopen: 'bg-blue-50 text-blue-700',
  on_hold: 'bg-amber-50 text-amber-700',
  closed: 'bg-slate-100 text-slate-600',
}

export default function MergeDrawer({
  open,
  onClose,
  candidates,
  defaultPrimaryId,
  onConfirm,
}: MergeDrawerProps): JSX.Element | null {
  const [removed, setRemoved] = useState<Set<string>>(new Set())
  const [primaryId, setPrimaryId] = useState(defaultPrimaryId)
  const [note, setNote] = useState('')
  const [noteIsPublic, setNoteIsPublic] = useState(false)
  const [addCc, setAddCc] = useState(false)
  const [allowDifferent, setAllowDifferent] = useState(false)
  const [busy, setBusy] = useState(false)
  // A rejection from the server, kept on screen next to the conversation it
  // names — the toast alone left the agent guessing which one was at fault.
  const [serverFailure, setServerFailure] = useState<
    { code?: string | undefined; conversationId?: string | undefined } | null
  >(null)

  const active = useMemo(
    () => candidates.filter((c) => !removed.has(c.id)),
    [candidates, removed],
  )

  // Requesters we can actually compare — an unknown requester isn't evidence
  // of a mismatch, so it must not trigger the warning on its own.
  const differentRequesters = useMemo(() => {
    const known = active.map((c) => c.requester).filter((r): r is string => Boolean(r))
    return new Set(known).size > 1
  }, [active])

  if (!open) return null

  const secondaries = active.filter((c) => c.id !== primaryId)
  const primary = active.find((c) => c.id === primaryId) ?? null

  // Every rule the set breaks, keyed by conversation, so each row can carry its
  // own explanation and the summary can list them all at once. Recomputed on
  // each render because changing the primary changes which rules apply.
  const blockers = new Map<string, MergeBlocker>()
  for (const c of active) {
    const blocker = mergeBlocker(c, c.id === primaryId ? 'primary' : 'secondary')
    if (blocker) blockers.set(c.id, blocker)
  }

  // A server rejection that named a conversation is attributed to that row;
  // one that named none (or a conversation no longer in the set) is shown on
  // its own.
  const failureCode = serverFailure?.code
  const failureRef = serverFailure?.conversationId
    ? active.find((c) => c.id === serverFailure.conversationId)?.referenceId
    : undefined
  const generalFailure =
    failureCode && !blockers.has(serverFailure?.conversationId ?? '')
      ? SERVER_ONLY_REASONS[failureCode] ??
        (failureRef
          ? `The server refused this merge because of #${failureRef} (${failureCode}).`
          : `The server refused this merge (${failureCode}).`)
      : null

  const canSubmit =
    !busy &&
    primary !== null &&
    secondaries.length > 0 &&
    secondaries.length <= 15 &&
    blockers.size === 0 &&
    (!differentRequesters || allowDifferent)

  const remove = (id: string): void => {
    setRemoved((s) => new Set(s).add(id))
    setServerFailure(null)
    // Removing the chosen primary must not leave the form without one.
    if (id === primaryId) {
      const next = candidates.find((c) => c.id !== id && !removed.has(c.id))
      if (next) setPrimaryId(next.id)
    }
  }

  const choosePrimary = (id: string): void => {
    setPrimaryId(id)
    // Which rules apply depends on the primary, so a stale rejection would be
    // misleading once it changes.
    setServerFailure(null)
  }

  const submit = async (): Promise<void> => {
    if (!canSubmit || !primary) return
    setBusy(true)
    setServerFailure(null)
    try {
      const result = await onConfirm({
        primaryId: primary.id,
        secondaryIds: secondaries.map((s) => s.id),
        note: note.trim(),
        noteIsPublic,
        addSecondaryRecipientsToCc: addCc,
        allowDifferentRequesters: allowDifferent,
      })
      if (result.ok) onClose()
      else setServerFailure(result.failure ?? {})
    } finally {
      setBusy(false)
    }
  }

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Merge conversations"
      subtitle="Choose which conversation to keep. The others are closed and their conversations move into it."
      icon={<GitMerge className="h-4 w-4" />}
      info={{
        lead: 'Folds several conversations about the same issue into one, so the customer and the agents work a single thread.',
        points: [
          { term: 'Primary', detail: 'The conversation you keep. It inherits every reply, note and attachment from the others.' },
          { term: 'Secondaries', detail: 'Closed and marked as merged into the primary, with a note recording where they went.' },
          { term: 'Different requesters', detail: 'Allowed, but you must confirm it — only the primary’s requester ends up owning the thread, so CC the others if they still need replies.' },
          { term: 'What blocks a merge', detail: 'A conversation already merged, in a parent/child relationship, linked to others, or awaiting approval cannot take part. Each one is named above with what to do about it.' },
          { term: 'Limit', detail: 'At most 15 conversations can be merged into a primary at once.' },
        ],
        caution: 'Merging cannot be undone.',
      }}
      width={640}
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void submit()} disabled={!canSubmit}>
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Merge {secondaries.length > 0 ? `${secondaries.length} conversation${secondaries.length === 1 ? '' : 's'}` : ''}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-5">
        {/* ── Primary conversation selection ── */}
        <div>
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Primary conversation
          </p>
          <div className="space-y-2">
            {active.map((c) => {
              const isPrimary = c.id === primaryId
              const blocker = blockers.get(c.id)
              return (
                <div
                  key={c.id}
                  className={`${R} border transition-all ${
                    blocker
                      ? 'border-red-200 bg-red-50/60'
                      : isPrimary
                        ? 'border-[#3366FF] bg-blue-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                  }`}
                >
                <div className="flex items-center gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => choosePrimary(c.id)}
                    className={`flex h-5 w-5 shrink-0 items-center justify-center ${R} border-2 transition-all ${
                      isPrimary
                        ? 'border-[#3366FF] bg-[#3366FF]'
                        : 'border-slate-300 bg-white hover:border-[#3366FF]/50'
                    }`}
                    aria-label={`Keep conversation ${c.referenceId} as the primary`}
                  >
                    {isPrimary && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#0f172a]">#{c.referenceId}</span>
                      {c.status && (
                        <span className={`${R} px-1.5 py-0.5 text-[10px] font-medium ${STATUS_PILL[c.status] ?? 'bg-slate-100 text-slate-600'}`}>
                          {c.status}
                        </span>
                      )}
                      {isPrimary && (
                        <span className={`${R} bg-[#3366FF]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#3366FF]`}>
                          Keeps everything
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{c.subject || '(No subject)'}</div>
                    {c.requester && <div className="truncate text-[11px] text-slate-400">{c.requester}</div>}
                  </div>
                  {active.length > 2 && (
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      className={`${R} border border-slate-200 p-1 text-slate-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500`}
                      title="Remove from this merge"
                      aria-label={`Remove conversation ${c.referenceId} from this merge`}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {/* Why this particular conversation can't take part, and what to
                    do about it — stated on the row itself, so it is obvious
                    which of five conversations is the problem. */}
                {blocker && (
                  <div className="flex items-start gap-2 border-t border-red-200 px-3 py-2 text-[11px] leading-relaxed text-red-700">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      <strong className="font-semibold">Can’t be merged — </strong>
                      {blocker.reason}
                      <span className="block text-red-600/90">{blocker.remedy}</span>
                      {failureCode === blocker.code && failureRef === c.referenceId && (
                        <span className="mt-0.5 block font-medium">The server rejected the last attempt for this reason.</span>
                      )}
                    </span>
                  </div>
                )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Blocked: one line per conversation that can't take part ──
            The rows above carry the same detail, but the summary is what the
            agent reads when the Merge button won't light up. */}
        {blockers.size > 0 && (
          <div className={`${R} border border-red-200 bg-red-50 p-3 text-xs text-red-800`}>
            <p className="flex items-start gap-2 font-semibold">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {blockers.size === 1
                ? 'One conversation in this set can’t be merged.'
                : `${blockers.size} conversations in this set can’t be merged.`}
            </p>
            <ul className="mt-1.5 space-y-1.5 pl-5">
              {[...blockers.entries()].map(([id, b]) => (
                <li key={id}>
                  <span>{b.reason}</span>
                  <span className="block text-red-700/90">{b.remedy}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 pl-5 text-red-700/90">
              Remove them from this merge, or fix the relationship, and the Merge button will enable.
            </p>
          </div>
        )}

        {/* ── The server refused, and named no conversation we still show ── */}
        {generalFailure && (
          <div className={`${R} border border-red-200 bg-red-50 p-3 text-xs text-red-800`}>
            <p className="flex items-start gap-2 font-semibold">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Merge rejected
            </p>
            <p className="mt-1 pl-5">{generalFailure}</p>
          </div>
        )}

        {/* ── Error: too many conversations ── */}
        {secondaries.length > 15 && (
          <div className={`${R} flex items-start gap-2 bg-red-50 p-3 text-xs text-red-700`}>
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>You can merge at most 15 conversations at a time. Remove {secondaries.length - 15} to continue.</span>
          </div>
        )}

        {/* ── Warning: different requesters ── */}
        {differentRequesters && (
          <div className={`${R} border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900`}>
            <p className="flex items-start gap-2 font-semibold">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              These conversations come from different requesters.
            </p>
            <p className="mt-1 pl-5 text-amber-800">
              Everything will end up on one conversation that only the primary requester owns. Add the others to
              CC below if they should keep receiving replies.
            </p>
            <label className="mt-2.5 flex items-center gap-2 pl-5 font-medium">
              <input
                type="checkbox"
                checked={allowDifferent}
                onChange={(e) => setAllowDifferent(e.target.checked)}
                className="h-4 w-4 rounded-[3px] border-slate-300 text-[#3366FF] focus:ring-[#EEF4FF]"
              />
              Merge anyway
            </label>
          </div>
        )}

        {/* ── Options ── */}
        <div className="space-y-3 border-t border-slate-100 pt-4">
          <label className="flex items-start gap-2.5 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded-[3px] border-slate-300 text-[#3366FF] focus:ring-[#EEF4FF]"
              checked={addCc}
              onChange={(e) => setAddCc(e.target.checked)}
            />
            <span>
              Add secondary conversation recipients to CC
              <span className="block text-xs text-slate-400">
                Everyone on the merged conversations stays in the loop on replies to the primary.
              </span>
            </span>
          </label>

          <Field label="Note">
            <textarea
              id="merge-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — leave blank to record which conversations were merged."
              className={textareaClass}
            />
          </Field>

          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={noteIsPublic}
              onChange={(e) => setNoteIsPublic(e.target.checked)}
              className="h-4 w-4 rounded-[3px] border-slate-300 text-[#3366FF] focus:ring-[#EEF4FF]"
            />
            Make this note public — the requester will see it and may be emailed
          </label>
        </div>

        {/* ── Summary ── */}
        <div className={`${R} bg-slate-50 p-3.5 text-xs leading-relaxed text-slate-600`}>
          <p className="font-semibold text-[#0f172a]">What happens</p>
          <ul className="mt-1.5 space-y-0.5">
            <li className="flex items-start gap-1.5">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
              <span>
                {secondaries.length} conversation{secondaries.length === 1 ? '' : 's'} will be closed and marked as
                merged into <strong className="text-slate-800">#{primary?.referenceId ?? '—'}</strong>.
              </span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
              <span>Their replies and notes move into the primary's thread, in date order.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
              <span>A note is added to every conversation recording the merge.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-400" />
              <span className="font-medium text-red-600">This can't be undone.</span>
            </li>
          </ul>
        </div>
      </div>
    </ActionDrawer>
  )
}
